import React, { useState } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { Theme } from '@theme'
import { useNavigation } from '@react-navigation/native'
import { useTheme } from '@react-navigation/native'
import { Text, Icon, IconName } from '../atoms'
import {
  formatHistoryDate,
  formatNoteMonth,
  getCommonStyles,
  groupNotesByDate,
  NoteSectionKey,
} from '@utils'
import { NoteItem } from '../molecules/NoteItem'
import { VoiceRecordButton } from '../molecules/VoiceRecordButton'
import {
  ContactHistoryItem,
  Note,
  NotesFilters,
  useCreationStackIntegration,
  useGetContactHistory,
  useGetNotesSummary,
  usePaginateNotes,
  useScreen,
} from '@hooks'
import { Button, NoteItemSkeletonList } from '../molecules'
import { useLanguage } from '@context'

const HISTORY_ICONS: Record<ContactHistoryItem['type'], IconName> = {
  note: 'note',
  message: 'messageCircle',
  event: 'calendar',
  keepContact: 'bell',
}

export const Notes = ({
  profileId,
  filters,
  isGhost = false,
  personalOnly = false,
  grouped = false,
  insights,
  onNotePress,
  onAddPress,
}: {
  // Notes about one contact (profile, conversation).
  profileId?: string
  // The Notes screen's search and chips. Without them the list is the one of
  // a contact and shows the "Ajouter une note" button.
  filters?: Omit<NotesFilters, 'type'>
  isGhost?: boolean
  // A group conversation: notes there are only ever personal (a group is not
  // a contact to share with).
  personalOnly?: boolean
  // Date sections (Aujourd'hui, Cette semaine, months) — the Notes screen.
  grouped?: boolean
  // A contact's profile: AI summary, « À retenir », « Dicter une note » and
  // « Historique avec X » in the Perso tab. Lines of the history that are not
  // notes are opened by the profile (conversation, Rendez-vous / Rappel tab).
  insights?: {
    firstName: string
    onHistoryPress?: (item: ContactHistoryItem) => void
  }
  onNotePress?: (noteId: string) => void
  onAddPress?: (isShared?: boolean) => void
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const commonStyles = getCommonStyles(theme)
  const navigation = useNavigation()
  const { isMobile } = useScreen()
  const { isCreationStackEnabled, showNoteCreation } = useCreationStackIntegration()

  // One list per tab, each loaded in full by usePaginateNotes, so the counts
  // are the real totals and no note is left beyond a first page.
  const baseFilters = { ...filters, contactId: profileId ?? filters?.contactId }
  const personalNotes = usePaginateNotes({ ...baseFilters, type: 'personal' })
  const sharedNotes = usePaginateNotes({ ...baseFilters, type: 'shared' })

  // "Perso" comes first and opens by default: personal notes are the heart of
  // a contact's memory. Ghost profiles have no shared tab (no account).
  const [isShared, setIsShared] = useState(false)
  const showShared = isShared && !isGhost && !personalOnly
  const activeList = showShared ? sharedNotes : personalNotes
  // The notes could not be loaded at all: said as such, not as « Aucune note ».
  // A refresh that fails with notes already on screen keeps them.
  const loadFailed = activeList.isLoadingError && !activeList.isFetching
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)

  // The summary and « À retenir » come from MY personal notes: they only
  // show under « Perso », so nobody can think the contact sees them.
  const showInsights = !!insights && !!profileId && !showShared
  const { data: summary } = useGetNotesSummary(profileId, showInsights)
  const { data: history = [] } = useGetContactHistory(profileId, showInsights && !isGhost)
  const pinnedNotes = showInsights ? personalNotes.flatData.filter((n) => n.isPinned) : []
  const notes = showInsights ? activeList.flatData.filter((n) => !n.isPinned) : activeList.flatData

  const handleAddNote = () => {
    // Ghost profiles can't own shared notes — they have no real account
    // to share with. Force private on the way out so EditNote doesn't
    // re-flip it.
    const effectiveIsShared = isGhost || personalOnly ? false : isShared
    if (onAddPress) {
      onAddPress(effectiveIsShared)
    } else if (isCreationStackEnabled) {
      showNoteCreation({
        isCreationStack: true,
        contactId: profileId,
        isGhost,
      })
    } else {
      ;(navigation as any).navigate('Menu', {
        screen: 'Notes',
        params: {
          screen: 'EditNote',
          params: {
            isShared: effectiveIsShared,
            contactId: profileId,
            isGhost,
          },
        },
      })
    }
  }

  const openNote = (noteId: string) => {
    if (onNotePress) {
      onNotePress(noteId)
      setSelectedNoteId(noteId)
    } else {
      ;(navigation as any).navigate('Menu', {
        screen: 'Notes',
        params: {
          screen: 'EditNote',
          params: {
            noteId,
            contactId: profileId,
            isGhost,
          },
        },
      })
    }
  }

  const sectionTitle = (key: NoteSectionKey) =>
    key === 'pinned'
      ? t('notes.sections.pinned')
      : key === 'today'
        ? t('notes.sections.today')
        : key === 'thisWeek'
          ? t('notes.sections.thisWeek')
          : formatNoteMonth(key)

  const renderNote = (note: Note) => (
    <NoteItem
      key={note.id}
      note={note}
      isSelected={!onNotePress && selectedNoteId === note.id}
      onPress={() => openNote(note.id)}
    />
  )

  const renderTab = (shared: boolean) => {
    const isActive = showShared === shared
    const list = shared ? sharedNotes : personalNotes
    return (
      <TouchableOpacity
        key={shared ? 'shared' : 'personal'}
        testID={shared ? 'notes-tab-shared' : 'notes-tab-personal'}
        onPress={() => setIsShared(shared)}
        style={[
          styles.rowFlex,
          !isGhost &&
            !personalOnly && {
              borderBottomWidth: 2,
              borderBottomColor: isActive ? theme.colors.primary : theme.colors.grey10,
            },
        ]}
      >
        <Icon
          name={shared ? 'user' : 'lock'}
          size={theme.iconSize.small}
          color={isActive ? theme.colors.primary : theme.colors.greyBlue}
        />
        <Text type="bodyBold">{shared ? t('common.shared') : t('common.personal')}</Text>
        {/* No « (0) » while the count is not known: loading, or failed. */}
        {list.data !== undefined && (
          <Text type="body" color={theme.colors.lightGrey}>
            {t('common.parenCount', { count: list.totalCount })}
          </Text>
        )}
      </TouchableOpacity>
    )
  }

  const dateLabels = { today: t('notes.dates.today'), yesterday: t('notes.dates.yesterday') }

  return (
    <View style={styles.noteContainer}>
      <View style={styles.row}>
        {renderTab(false)}
        {!isGhost && !personalOnly && renderTab(true)}
      </View>
      {showInsights && !!summary && (
        <View testID="notes-summary" style={styles.summaryCard}>
          <View style={[commonStyles.row, styles.summaryHeader]}>
            <Icon name="star" size={theme.iconSize.small} />
            <Text type="bodyBold">{t('notes.summaryTitle', { name: insights.firstName })}</Text>
          </View>
          <Text type="bodySmall" style={styles.summaryText}>
            {summary}
          </Text>
          <Text type="bodyExtraSmall" color={theme.colors.lightGrey}>
            {t('notes.summaryFootnote')}
          </Text>
        </View>
      )}
      {pinnedNotes.map((note) => (
        <TouchableOpacity
          key={note.id}
          testID="notes-to-remember"
          style={[commonStyles.row, styles.toRemember]}
          onPress={() => openNote(note.id)}
        >
          <Icon name="pin" size={theme.iconSize.small} color={theme.colors.primary} />
          <Text type="bodySmall" style={commonStyles.flex} numberOfLines={2}>
            <Text type="bodySmallBold">{t('notes.toRemember')} </Text>
            {note.content}
          </Text>
        </TouchableOpacity>
      ))}
      {!filters && (
        <View style={[styles.buttonsRow, !isMobile && styles.buttonsRowCentered]}>
          <View style={showInsights && styles.buttonBeside}>
            <Button
              isSmall
              style={[styles.buttonAdd, showInsights && styles.buttonAddFill]}
              title={t('notesForm.addNote')}
              textColor={theme.colors.primary}
              isIconRight
              iconName="edit"
              iconColor={theme.colors.primary}
              onPress={handleAddNote}
            />
          </View>
          {showInsights && (
            <View style={styles.buttonBeside}>
              <VoiceRecordButton profileId={profileId} variant="pill" />
            </View>
          )}
        </View>
      )}
      {activeList.isLoading ? (
        <NoteItemSkeletonList count={5} />
      ) : grouped ? (
        groupNotesByDate(notes).map((section) => (
          <View key={section.key}>
            <Text type="bodySmallBold" color={theme.colors.greyBlue} style={styles.sectionTitle}>
              {sectionTitle(section.key)}
            </Text>
            {section.data.map(renderNote)}
          </View>
        ))
      ) : (
        notes.map(renderNote)
      )}
      {loadFailed && (
        <View testID="notes-load-error" style={styles.emptyContainer}>
          <Text type="body" style={styles.emptyText}>
            {t('notes.loadError.title')}
          </Text>
          <Text type="body" style={styles.emptySubtext}>
            {t('notes.loadError.subtitle')}
          </Text>
          <TouchableOpacity style={styles.retry} onPress={() => void activeList.refetch()}>
            <Text type="bodyBold" color={theme.colors.primary}>
              {t('common.retry')}
            </Text>
          </TouchableOpacity>
        </View>
      )}
      {activeList.isFetchNextPageError && !activeList.isFetchingNextPage && (
        <View testID="notes-more-error" style={styles.moreError}>
          <Text type="bodySmall" color={theme.colors.greyBlue}>
            {t('notes.loadError.partial')}
          </Text>
          <TouchableOpacity onPress={() => void activeList.fetchNextPage()}>
            <Text type="bodySmallBold" color={theme.colors.primary}>
              {t('common.retry')}
            </Text>
          </TouchableOpacity>
        </View>
      )}
      {activeList.isFetched &&
        !activeList.isLoading &&
        !activeList.isLoadingError &&
        activeList.flatData.length === 0 && (
          <View style={styles.emptyContainer}>
            <Text type="body" style={styles.emptyText}>
              {showShared ? t('notes.empty.titleShared') : t('notes.empty.titlePersonal')}
            </Text>
            <Text type="body" style={styles.emptySubtext}>
              {filters?.q ? t('notes.empty.noMatch') : t('notes.empty.pressPlusToCreate')}
            </Text>
          </View>
        )}
      {showInsights && !isGhost && history.length > 0 && (
        <View testID="contact-history" style={styles.history}>
          <Text type="bodyBold" color={theme.colors.greyBlue} style={styles.historyTitle}>
            {t('notes.historyTitle', { name: insights.firstName })}
          </Text>
          {history.map((item, index) => (
            <TouchableOpacity
              key={`${item.type}-${item.id}`}
              style={[
                commonStyles.row,
                styles.historyRow,
                index === history.length - 1 && styles.historyRowLast,
              ]}
              onPress={() =>
                item.type === 'note' ? openNote(item.id) : insights.onHistoryPress?.(item)
              }
            >
              <Icon name={HISTORY_ICONS[item.type]} size={20} color={theme.colors.primary} />
              <Text type="body" style={commonStyles.flex} numberOfLines={1}>
                {t('notes.history.line', { type: t(`notes.history.${item.type}`) })}
                {item.type === 'note' ? <Text type="bodyBold">{item.title}</Text> : item.title}
              </Text>
              <Text type="bodySmall" color={theme.colors.lightGrey}>
                {formatHistoryDate(item.date, dateLabels)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    noteContainer: {
      padding: theme.spacing.lg,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingBottom: theme.spacing.sm,
    },
    rowFlex: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.xs,
      paddingBottom: theme.spacing.sm,
    },
    sectionTitle: {
      marginTop: theme.spacing.xl,
      paddingHorizontal: theme.spacing.sm,
    },
    summaryCard: {
      marginTop: theme.spacing.lg,
      padding: theme.spacing.md,
      gap: theme.spacing.xs,
      borderRadius: theme.border.large,
      borderWidth: 1,
      borderColor: theme.colors.primary + '40',
      backgroundColor: theme.colors.sidebar,
    },
    summaryHeader: {
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    summaryText: {
      lineHeight: 20,
      color: theme.colors.text,
    },
    toRemember: {
      alignItems: 'center',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.sm,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.sidebar,
    },
    buttonsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.sm,
    },
    buttonsRowCentered: {
      justifyContent: 'center',
    },
    buttonAdd: {
      height: undefined,
      minWidth: 250,
      marginTop: theme.spacing.sm,
      marginHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.sm,
      backgroundColor: theme.colors.primary + '10',
      borderWidth: 1,
      borderColor: theme.colors.primary,
    },
    // Two buttons side by side, same size: each takes half of the row (at
    // most 300 wide), without the lone button's minimum width and margins.
    buttonBeside: {
      flex: 1,
      maxWidth: 300,
      marginTop: theme.spacing.sm,
    },
    buttonAddFill: {
      minWidth: 0,
      width: '100%',
      marginTop: 0,
      marginHorizontal: 0,
    },
    emptyContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 60,
      paddingHorizontal: 40,
    },
    emptyText: {
      fontSize: theme.fontSize.medium,
      fontWeight: '600',
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 8,
    },
    emptySubtext: {
      fontSize: theme.fontSize.small,
      color: theme.colors.greyBlue,
      textAlign: 'center',
    },
    retry: {
      marginTop: theme.spacing.md,
      padding: theme.spacing.sm,
    },
    moreError: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
      paddingVertical: theme.spacing.lg,
    },
    history: {
      marginTop: theme.spacing.xl,
      paddingTop: theme.spacing.lg,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey20,
    },
    historyTitle: {
      marginBottom: theme.spacing.xs,
    },
    historyRow: {
      alignItems: 'center',
      gap: theme.spacing.md,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.xs,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.grey20,
    },
    historyRowLast: {
      borderBottomWidth: 0,
    },
  })
